import {
  resolveItemTileTitle,
  type SectionItemPresentationVm,
} from '../viewModels/newHome.vm';

/** Shared with classic home / WebEngage naming for offer-style carousel tiles. */
export const HOME_PAGE_BANNER_CLICKED = 'Home Page Banner Clicked';

/** Fired when the user moves to another slide (swipe / arrows / tap peek), matching classic home carousel behavior. */
export const HOME_PAGE_BANNER_VIEWED = 'Home Page Banner Viewed';

export const NEW_HOME_BANNER_CAROUSEL_SECTION_NAME = 'Banner Carousel';

export const NEW_HOME_BANNER_TYPE_OFFER = 'Offer';

/** Handpicked / collection strip — matches `Home Page Banner Clicked` for this section. */
export const NEW_HOME_HANDPICKED_BANNER_SECTION_NAME = 'Collections';

export const NEW_HOME_BANNER_TYPE_COLLECTION = 'Collection';

/** Core payload for collection/handpicked banners (viewed + clicked). */
export function newHomeCollectionBannerBaseEventProperties(options: {
  bannerDisplayName: string;
  bannerPosition: number;
}): Record<string, string | number> {
  return {
    'Banner Name': options.bannerDisplayName,
    'Banner Position': options.bannerPosition,
    'Section Name': NEW_HOME_HANDPICKED_BANNER_SECTION_NAME,
    'Banner Type': NEW_HOME_BANNER_TYPE_COLLECTION,
  };
}

/** Core payload for offer banners (viewed + clicked). Click helper may add Target URL. */
export function newHomeOfferBannerBaseEventProperties(options: {
  bannerDisplayName: string;
  bannerPosition: number;
}): Record<string, string | number> {
  return {
    'Banner Name': options.bannerDisplayName,
    'Banner Position': options.bannerPosition,
    'Section Name': NEW_HOME_BANNER_CAROUSEL_SECTION_NAME,
    'Banner Type': NEW_HOME_BANNER_TYPE_OFFER,
  };
}

export type ParallaxBannerSlideFields = {
  id?: string | number;
  title?: string;
  tag?: string;
  alt?: string;
  href?: string;
  categoryName?: string;
  shouldRefresh?: boolean;
};

/**
 * Banner label for analytics: tile title (incl. desktop variant), then category/collection, then raw title.
 */
export function resolveNewHomeOfferBannerDisplayName(
  item: SectionItemPresentationVm,
  isDesktop: boolean,
): string {
  const primary =
    resolveItemTileTitle(item, isDesktop).trim() ||
    item.categoryName?.trim() ||
    item.collection?.trim() ||
    '';
  return primary || item.title;
}

/** Same resolution order as tile items, for parallax slide objects built from JSON. */
export function resolveNewHomeOfferBannerDisplayNameFromSlide(
  slide: ParallaxBannerSlideFields,
): string {
  return (
    (slide.title && slide.title.trim()) ||
    (slide.tag && slide.tag.trim()) ||
    (slide.alt && slide.alt.trim()) ||
    (slide.categoryName && slide.categoryName.trim()) ||
    ''
  );
}

export function bannerPositionFromSlideId(id: string | number | undefined): number {
  if (typeof id === 'number' && Number.isFinite(id)) return id;
  if (typeof id === 'string' && id.trim() !== '' && Number.isFinite(Number(id))) {
    return Number(id);
  }
  return 0;
}

export function newHomeOfferBannerClickEventProperties(options: {
  bannerDisplayName: string;
  bannerPosition: number;
  targetUrl?: string;
}): Record<string, string | number> {
  return {
    ...newHomeOfferBannerBaseEventProperties(options),
    ...(options.targetUrl ? { 'Target URL': options.targetUrl } : {}),
  };
}
